import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { failuresApi } from '@/services/api/failures';
import { WeibullAnalysisResult } from '@/utils/weibull';
import {
  calculateCensoredWeibullParameters,
  CensoredWeibullResult,
  isCensoredObservation,
} from '@/utils/weibullCensored';
import {
  estimateWeibullMLE,
  prepareMLEData,
  MLEResult,
  MLEConfig,
} from '@/utils/weibullMLE';
import {
  calculatePlottingPositions,
  validatePlottingPositions
} from '@/utils/plottingPositions';
import { confidenceIntervals } from '@/utils/profileLikelihood';
import { FailureFilters, DEFAULT_FAILURE_FILTERS } from '@/types';
import { FailureRecord } from '@/types/failure';

export type AnalysisMethod = 'johnson' | 'mle';

export interface MethodRecommendation {
  method: AnalysisMethod;
  reason: string;
  confidence: 'low' | 'medium' | 'high';
}

/**
 * MLE results with Profile Likelihood confidence intervals
 */
function calculateMLE(
  mleResult: MLEResult,
  data: FailureRecord[],
  confidenceLevel: number = 0.95
): MLEResult {
  const mleData = prepareMLEData(data);
  const censoringRate = mleData.filter(p => p.isCensored).length / mleData.length;

  // Check if enhancement is needed
  const hasNarrowCI = mleResult.confidenceIntervals &&
    (mleResult.confidenceIntervals.shape.upper - mleResult.confidenceIntervals.shape.lower) < 0.01;
  const hasFisherIssues = mleResult.fisherInformation.determinant <= 0;
  const highCensoring = censoringRate > 0.3;

  const needsEnhancement = hasNarrowCI || hasFisherIssues || highCensoring;

  if (!needsEnhancement) {
    return mleResult;
  }

  try {
    // Use Profile Likelihood confidence intervals
    const robustCI = confidenceIntervals(
      mleResult.parameters.logLikelihood,
      mleResult.parameters.shape,
      mleResult.parameters.scale,
      mleData,
      confidenceLevel
    );

    // Return MLE result
    return {
      ...mleResult,
      confidenceIntervals: {
        shape: robustCI.shape,
        scale: robustCI.scale,
        method: robustCI.method
      },
      // Update standard errors based on new CI widths (approximate)
      standardErrors: {
        shape: (robustCI.shape.upper - robustCI.shape.lower) / (2 * 1.96),
        scale: (robustCI.scale.upper - robustCI.scale.lower) / (2 * 1.96)
      }
    };

  } catch (error) {
    // Profile Likelihood enhancement failed - return original MLE result
    return mleResult;
  }
}

/**
 * Convert MLE result to standard WeibullAnalysisResult format
 */
export function convertMleResult(
  mleResult: MLEResult,
  rawData: FailureRecord[],
  confidenceLevel: number = 0.95
): WeibullAnalysisResult {
  const mleResultWithCI = calculateMLE(mleResult, rawData, confidenceLevel);

  // Use shared plotting position utility for consistency
  const plottingResult = calculatePlottingPositions(rawData, 'MLE Method');
  const validation = validatePlottingPositions(plottingResult);

  if (!validation.isValid) {
    // Validation failed - results may be unreliable
  }

  const operationTimes = plottingResult.times;
  const probabilities = plottingResult.probabilities;
  const failureRecords = plottingResult.records;
  // Generate fitted line data for plotting with log-spaced points for smooth curves
  const minTime = Math.max(0.1, Math.min(...rawData.map(d => d.operation_time_years)));
  const maxTime = Math.max(...rawData.map(d => d.operation_time_years));

  // Generate log-spaced time points for better curve on log-scale plots
  const logMin = Math.log(minTime);
  const logMax = Math.log(maxTime);
  const timePoints = Array.from({ length: 100 }, (_, i) => {
    return Math.exp(logMin + (i / 99) * (logMax - logMin));
  });

  const fittedLine = timePoints.map(t => {
    const probability = 1 - Math.exp(-Math.pow(t / mleResultWithCI.parameters.scale, mleResultWithCI.parameters.shape));
    return { x: t, y: probability };
  });

  // Extract only failure records for plotting and sort by operation time
  const failures = rawData.filter(f => !isCensoredObservation(f))
    .sort((a, b) => a.operation_time_years - b.operation_time_years);

  // Transform failure records
  const transformedFailureRecords = failureRecords.map((f, i) => ({
    case_id: String(f.case_id),
    turbine_id: f.turbine_name,
    site_name: f.site_name,
    component_major: f.component_major,
    component_minor: f.component_minor,
    failure_mode: f.failure_mode,
    outcome_name: f.outcome_name,
    failure_date: f.failure_date,
    operation_time_years: operationTimes[i],
    turbine_model: f.turbine_model,
    turbine_name: f.turbine_name,
  }));

  // Generate confidence bands
  let confidenceBands;
  const hasConfidenceIntervals = !!(mleResultWithCI.confidenceIntervals?.shape && mleResultWithCI.confidenceIntervals?.scale);
  const hasValidStandardErrors = mleResultWithCI.standardErrors.shape > 0 &&
    mleResultWithCI.standardErrors.scale > 0 &&
    Number.isFinite(mleResultWithCI.standardErrors.shape) &&
    Number.isFinite(mleResultWithCI.standardErrors.scale);

  // Generate confidence bands from available confidence intervals or standard errors
  if (hasConfidenceIntervals) {
    // Use all parameter combinations to account for correlation
    const parameterCombinations = [
      { shape: mleResultWithCI.confidenceIntervals!.shape!.lower, scale: mleResultWithCI.confidenceIntervals!.scale!.lower },
      { shape: mleResultWithCI.confidenceIntervals!.shape!.lower, scale: mleResultWithCI.confidenceIntervals!.scale!.upper },
      { shape: mleResultWithCI.confidenceIntervals!.shape!.upper, scale: mleResultWithCI.confidenceIntervals!.scale!.lower },
      { shape: mleResultWithCI.confidenceIntervals!.shape!.upper, scale: mleResultWithCI.confidenceIntervals!.scale!.upper }
    ];

    const lowerBand = timePoints.map(t => {
      const probabilities = parameterCombinations.map(({ shape, scale }) => {
        return 1 - Math.exp(-Math.pow(t / Math.max(0.01, scale), Math.max(0.01, shape)));
      });
      const minProb = Math.min(...probabilities);
      return { x: t, y: Math.max(0.0001, Math.min(0.9999, minProb)) };
    });

    const upperBand = timePoints.map(t => {
      const probabilities = parameterCombinations.map(({ shape, scale }) => {
        return 1 - Math.exp(-Math.pow(t / Math.max(0.01, scale), Math.max(0.01, shape)));
      });
      const maxProb = Math.max(...probabilities);
      return { x: t, y: Math.max(0.0001, Math.min(0.9999, maxProb)) };
    });

    confidenceBands = { lower: lowerBand, upper: upperBand };

  } else if (hasValidStandardErrors) {
    // Use standard error-based confidence bands as fallback
    const zCritical = confidenceLevel === 0.99 ? 2.576 :
      confidenceLevel === 0.90 ? 1.645 : 1.96;

    const lowerBand = timePoints.map(t => {
      const optimisticShape = Math.max(0.01, mleResultWithCI.parameters.shape - zCritical * mleResultWithCI.standardErrors.shape);
      const optimisticScale = Math.max(0.01, mleResultWithCI.parameters.scale + zCritical * mleResultWithCI.standardErrors.scale);
      const lowerProb = 1 - Math.exp(-Math.pow(t / optimisticScale, optimisticShape));
      return { x: t, y: Math.max(0.0001, Math.min(0.9999, lowerProb)) };
    });

    const upperBand = timePoints.map(t => {
      const pessimisticShape = mleResultWithCI.parameters.shape + zCritical * mleResultWithCI.standardErrors.shape;
      const pessimisticScale = Math.max(0.01, mleResultWithCI.parameters.scale - zCritical * mleResultWithCI.standardErrors.scale);
      const upperProb = 1 - Math.exp(-Math.pow(t / pessimisticScale, pessimisticShape));
      return { x: t, y: Math.max(0.0001, Math.min(0.9999, upperProb)) };
    });

    confidenceBands = { lower: lowerBand, upper: upperBand };
  }

  // Calculate goodness-of-fit using R² statistic
  const goodnessOfFit = calculateRSquared(operationTimes, probabilities, mleResultWithCI.parameters.shape, mleResultWithCI.parameters.scale);

  // Calculate summary statistics
  const meanLife = mleResultWithCI.parameters.scale * Math.exp(Math.log(gamma(1 + 1 / mleResultWithCI.parameters.shape)));
  const medianLife = mleResultWithCI.parameters.scale * Math.pow(Math.log(2), 1 / mleResultWithCI.parameters.shape);

  return {
    parameters: {
      shape: mleResultWithCI.parameters.shape,
      scale: mleResultWithCI.parameters.scale,
      rSquared: goodnessOfFit, // Anderson-Darling based goodness-of-fit (industry standard)
      shapeCI: mleResultWithCI.confidenceIntervals?.shape,
      scaleCI: mleResultWithCI.confidenceIntervals?.scale,
    },
    plotData: {
      operationTimes,
      probabilities,
      fittedLine,
      failureRecords: transformedFailureRecords,
      confidenceBands,
    },
    summary: {
      sampleSize: failures.length,
      meanLife,
      medianLife,
      characteristicLife: mleResultWithCI.parameters.scale,
    },
    statistics: {
      standardErrors: {
        shape: mleResultWithCI.standardErrors.shape,
        scale: mleResultWithCI.standardErrors.scale,
      },
      confidenceLevel: confidenceLevel,
      degreesOfFreedom: Math.max(1, failures.length - 2), // n - p (2 parameters)
    },
    mleResult: mleResultWithCI,
  };
}

/**
 * Calculate R² goodness-of-fit statistic for Weibull distribution fit
 */
function calculateRSquared(operationTimes: number[], empiricalProbs: number[], shape: number, scale: number): number {
  if (operationTimes.length !== empiricalProbs.length) {
    return 0;
  }

  if (operationTimes.length < 3) {
    return 0.5; // Return neutral value for very small samples
  }

  const predicted = operationTimes.map(t => 1 - Math.exp(-Math.pow(t / scale, shape)));
  const meanEmpirical = empiricalProbs.reduce((a, b) => a + b) / empiricalProbs.length;

  const ssRes = empiricalProbs.reduce((sum, emp, i) => sum + Math.pow(emp - predicted[i], 2), 0);
  const ssTot = empiricalProbs.reduce((sum, emp) => sum + Math.pow(emp - meanEmpirical, 2), 0);

  const rSquared = ssTot === 0 ? 0 : Math.max(0, 1 - ssRes / ssTot);

  return rSquared;
}

/**
 * Debug utility to compare MLE vs Johnson's parameters
 * Can be used to validate that both methods produce reasonable results
 */
export function compareMethodParameters(
  johnsonAnalysis: WeibullAnalysisResult | null,
  mleAnalysis: WeibullAnalysisResult | null
) {
  if (!johnsonAnalysis || !mleAnalysis) {
    return null;
  }

  const shapeDiff = Math.abs(mleAnalysis.parameters.shape - johnsonAnalysis.parameters.shape);
  const scaleDiff = Math.abs(mleAnalysis.parameters.scale - johnsonAnalysis.parameters.scale);

  const shapeRelativeDiff = (shapeDiff / johnsonAnalysis.parameters.shape) * 100;
  const scaleRelativeDiff = (scaleDiff / johnsonAnalysis.parameters.scale) * 100;

  return {
    johnson: {
      shape: johnsonAnalysis.parameters.shape,
      scale: johnsonAnalysis.parameters.scale,
      rSquared: johnsonAnalysis.parameters.rSquared
    },
    mle: {
      shape: mleAnalysis.parameters.shape,
      scale: mleAnalysis.parameters.scale,
      rSquared: mleAnalysis.parameters.rSquared
    },
    differences: {
      shape: { absolute: shapeDiff, relative: shapeRelativeDiff },
      scale: { absolute: scaleDiff, relative: scaleRelativeDiff }
    },
    flags: {
      largeShapeDifference: shapeRelativeDiff > 20,
      largeScaleDifference: scaleRelativeDiff > 20,
      mleHasConfidenceIntervals: !!(mleAnalysis.parameters.shapeCI && mleAnalysis.parameters.scaleCI)
    }
  };
}

// Gamma function helper (moved from WeibullAnalysisPanel to avoid duplication)
function gamma(z: number): number {
  if (z < 0.5) {
    return Math.PI / (Math.sin(Math.PI * z) * gamma(1 - z));
  }
  z -= 1;
  const x =
    0.99999999999980993 +
    676.5203681218851 / (z + 1) +
    -1259.1392167224028 / (z + 2) +
    771.32342877765313 / (z + 3) +
    -176.61502916214059 / (z + 4) +
    12.507343278686905 / (z + 5) +
    -0.13857109526572012 / (z + 6) +
    9.9843695780195716e-6 / (z + 7) +
    1.5056327351493116e-7 / (z + 8);

  const t = z + 7.5;
  return Math.sqrt(2 * Math.PI) * Math.pow(t, z + 0.5) * Math.exp(-t) * x;
}

/**
 * Convert censored analysis result to standard WeibullAnalysisResult format
 */
export function convertCensoredResult(
  censoredResult: CensoredWeibullResult
): WeibullAnalysisResult {

  // Extract operation times and probabilities from failure points only
  const operationTimes = censoredResult.plotData.failures.map((f) => f.time);
  const probabilities = censoredResult.plotData.failures.map((f) => f.medianRank!);

  // Transform failure records to expected format
  const failureRecords = censoredResult.plotData.failures.map((f) =>
    f.failureRecord
      ? {
        case_id: String(f.failureRecord.case_id),
        turbine_id: f.failureRecord.turbine_name,
        site_name: f.failureRecord.site_name,
        component_major: f.failureRecord.component_major,
        component_minor: f.failureRecord.component_minor,
        failure_mode: f.failureRecord.failure_mode,
        outcome_name: f.failureRecord.outcome_name,
        failure_date: f.failureRecord.failure_date,
        operation_time_years: f.time,
        turbine_model: f.failureRecord.turbine_model,
        turbine_name: f.failureRecord.turbine_name,
      }
      : undefined
  );

  return {
    parameters: censoredResult.parameters,
    plotData: {
      operationTimes,
      probabilities,
      fittedLine: censoredResult.plotData.fittedLine,
      failureRecords: failureRecords, // Keep array to maintain index alignment
      confidenceBands: censoredResult.plotData.confidenceBands,
    },
    summary: {
      sampleSize: censoredResult.summary.failureCount, // Use failure count for compatibility
      meanLife: censoredResult.summary.meanLife,
      medianLife: censoredResult.summary.medianLife,
      characteristicLife: censoredResult.summary.characteristicLife,
    },
    statistics: censoredResult.statistics,
  };
}

/**
 * Get method recommendation based on data characteristics
 */
export function getMethodRecommendation(
  censoringPercentage: number,
  sampleSize: number
): MethodRecommendation {
  if (sampleSize < 10) {
    return {
      method: 'mle',
      reason: 'Small sample size - MLE provides robust estimates with proper uncertainty',
      confidence: 'medium'
    };
  }

  if (censoringPercentage < 30) {
    return {
      method: 'mle',
      reason: 'Low censoring - MLE provides optimal statistical properties',
      confidence: 'high'
    };
  } else if (censoringPercentage < 50) {
    return {
      method: 'mle',
      reason: 'Moderate censoring - MLE provides tighter confidence intervals',
      confidence: 'high'
    };
  } else if (censoringPercentage < 70) {
    return {
      method: 'mle',
      reason: 'High censoring - MLE significantly more accurate',
      confidence: 'high'
    };
  } else {
    return {
      method: 'mle',
      reason: 'Very high censoring - MLE essential for reliable results',
      confidence: 'high'
    };
  }
}

/**
 * Hook for real-time Weibull analysis with method selection support
 * Returns standard format
 */
export function useWeibullAnalysis(
  filters: FailureFilters = DEFAULT_FAILURE_FILTERS,
  confidenceLevel: number = 0.95,
  method?: AnalysisMethod
) {
  // Get complete failure dataset for analysis using real API
  const {
    data: analysisData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['failures-analysis', filters, method, confidenceLevel],
    queryFn: () => failuresApi.getFailuresForWeibullAnalysis(filters),
    staleTime: 60000, // 1 minute
    cacheTime: 600000, // 10 minutes
    refetchOnWindowFocus: false,
  });

  // Calculate analysis metadata for method selection
  const metadata = useMemo(() => {
    if (!analysisData?.failures) return null;

    const censoredCount = analysisData.failures.filter((f) => isCensoredObservation(f)).length;
    const failureCount = analysisData.failures.length - censoredCount;
    const censoringPercentage = (censoredCount / analysisData.failures.length) * 100;

    return {
      totalSamples: analysisData.failures.length,
      failureCount,
      censoredCount,
      censoringPercentage,
      hasCensoredData: censoredCount > 0,
    };
  }, [analysisData]);

  // Get method recommendation
  const recommendation = useMemo(() => {
    if (!metadata) return null;
    return getMethodRecommendation(metadata.censoringPercentage, metadata.totalSamples);
  }, [metadata]);

  // Determine which method to use
  const selectedMethod = method || recommendation?.method || 'mle';

  // Calculate Weibull analysis using selected method
  const analysis = useMemo((): WeibullAnalysisResult | null => {
    if (!analysisData?.failures || analysisData.failures.length < 3) {
      return null;
    }

    try {
      if (selectedMethod === 'mle') {
        // First calculate Johnson's method to get better initial values for MLE
        const censoredResult = calculateCensoredWeibullParameters(analysisData.failures, confidenceLevel);

        // Use Johnson's method parameters as initial values for MLE
        const johnsonInitialValues = {
          shape: censoredResult.parameters.shape,
          scale: censoredResult.parameters.scale
        };

        // Use MLE method with Johnson's method as starting point
        const mleData = prepareMLEData(analysisData.failures);

        // Adaptive MLE configuration based on data characteristics
        const censoringRate = mleData.filter(p => p.isCensored).length / mleData.length;

        const mleConfig: Partial<MLEConfig> = {
          confidenceLevel: confidenceLevel as (0.90 | 0.95 | 0.99),
          maxIterations: censoringRate > 0.6 ? 500 : censoringRate > 0.4 ? 350 : 200, // Much more iterations
          tolerance: censoringRate > 0.7 ? 1e-5 : censoringRate > 0.5 ? 5e-6 : 1e-6, // More relaxed tolerance
          initialValues: johnsonInitialValues,
          stepSizeFactor: censoringRate > 0.7 ? 0.1 : censoringRate > 0.5 ? 0.2 : 0.3, // Even more conservative
          // Wind turbine specific parameter bounds (detect if data is in years vs hours)
          parameterBounds: (() => {
            const maxTime = Math.max(...analysisData.failures.map(f => f.operation_time_years));
            const minTime = Math.min(...analysisData.failures.map(f => f.operation_time_years));

            // Better bounds detection based on data characteristics
            if (maxTime > 100) {
              return {
                shape: { min: 0.1, max: 15 },
                scale: { min: Math.max(100, minTime * 0.5), max: maxTime * 2 }, // Adaptive scale bounds
              };
            } else {
              return {
                shape: { min: 0.1, max: 15 },
                scale: { min: Math.max(0.5, minTime * 0.5), max: maxTime * 2 }, // Adaptive scale bounds
              };
            }
          })(),
        };

        const mleResult = estimateWeibullMLE(mleData, mleConfig);

        return convertMleResult(mleResult, analysisData.failures, confidenceLevel);
      } else {
        // Use Johnson's method (censored analysis)
        const censoredResult = calculateCensoredWeibullParameters(analysisData.failures, confidenceLevel);
        return convertCensoredResult(censoredResult);
      }
    } catch (error) {
      console.error('Weibull analysis failed:', error);
      return null;
    }
  }, [analysisData, confidenceLevel, selectedMethod]);

  // Calculate diversity metrics from the same dataset
  const diversityMetrics = useMemo(() => {
    if (!analysisData?.failures) {
      return {
        uniqueSites: 0,
        uniqueTurbines: 0,
        uniqueComponents: 0,
      };
    }

    return {
      uniqueSites: new Set(analysisData.failures.map((f) => f.site_name)).size,
      uniqueTurbines: new Set(analysisData.failures.map((f) => f.turbine_name)).size,
      uniqueComponents: new Set(analysisData.failures.map((f) => f.component_major)).size,
    };
  }, [analysisData]);

  return {
    analysis,
    isLoading,
    error,
    hasEnoughData: (analysisData?.failures?.length || 0) >= 3,
    sampleSize: analysisData?.failures?.length || 0,
    metadata,
    diversityMetrics,
    selectedMethod,
    recommendation,
  };
}

/**
 * Hook for component-specific Weibull analysis with multi-select components
 */
export function useComponentWeibullAnalysis(
  filters: FailureFilters = DEFAULT_FAILURE_FILTERS,
  components: string[]
) {
  const componentFilters: FailureFilters = {
    ...filters,
    componentsMajor: components,
  };

  return useWeibullAnalysis(componentFilters, 0.95);
}

/**
 * Hook for site-specific Weibull analysis with multi-select sites
 */
export function useSiteWeibullAnalysis(
  filters: FailureFilters = DEFAULT_FAILURE_FILTERS,
  sites: string[]
) {
  const siteFilters: FailureFilters = {
    ...filters,
    sites: sites,
  };

  return useWeibullAnalysis(siteFilters, 0.95);
}

/**
 * Hook for comparative Weibull analysis between different filter sets
 */
export function useComparativeWeibullAnalysis(filterSets: FailureFilters[]) {
  // Use a static approach to avoid hook call in callback
  const analysis1 = useWeibullAnalysis(filterSets[0] || DEFAULT_FAILURE_FILTERS, 0.95);
  const analysis2 = useWeibullAnalysis(filterSets[1] || DEFAULT_FAILURE_FILTERS, 0.95);
  const analysis3 = useWeibullAnalysis(filterSets[2] || DEFAULT_FAILURE_FILTERS, 0.95);

  // Collect only the analyses for the provided filter sets
  const analyses = [
    filterSets[0] ? analysis1 : null,
    filterSets[1] ? analysis2 : null,
    filterSets[2] ? analysis3 : null,
  ].filter(Boolean);

  return {
    analyses: analyses.map((a) => a.analysis).filter((a) => a !== null),
    isLoading: analyses.some((a) => a.isLoading),
    hasErrors: analyses.some((a) => a.error),
    validAnalyses: analyses.filter((a) => a.hasEnoughData).length,
    totalSampleSize: analyses.reduce((sum, a) => sum + a.sampleSize, 0),
    metadata: analyses.map((a) => a.metadata).filter(Boolean),
  };
}

/**
 * Hook for filtered Weibull analysis with automatic data quality checks
 */
export function useQualityFilteredWeibullAnalysis(
  filters: FailureFilters = DEFAULT_FAILURE_FILTERS,
  qualityOptions: {
    minOperationTime?: number;
    maxOperationTime?: number;
    excludeZeroTimes?: boolean;
    minSampleSize?: number;
  } = {}
) {
  // Apply quality filters
  const qualityFilters: FailureFilters = {
    ...filters,
    operationTimeMin: qualityOptions.minOperationTime || filters.operationTimeMin,
    operationTimeMax: qualityOptions.maxOperationTime || filters.operationTimeMax,
  };

  const { analysis, isLoading, error, hasEnoughData, sampleSize, metadata } = useWeibullAnalysis(
    qualityFilters,
    0.95
  );

  // Additional quality checks
  const qualityMetrics = useMemo(() => {
    if (!analysis) return null;

    const operationTimes = analysis.plotData.operationTimes;

    const zeroCount = operationTimes.filter((t) => t === 0).length;
    const outliers = operationTimes.filter((t) => {
      const q1 = operationTimes[Math.floor(operationTimes.length * 0.25)];
      const q3 = operationTimes[Math.floor(operationTimes.length * 0.75)];
      const iqr = q3 - q1;
      return t < q1 - 1.5 * iqr || t > q3 + 1.5 * iqr;
    }).length;

    return {
      zeroCount,
      outlierCount: outliers,
      dataQuality:
        analysis.parameters.rSquared > 0.9
          ? 'excellent'
          : analysis.parameters.rSquared > 0.8
            ? 'good'
            : analysis.parameters.rSquared > 0.7
              ? 'fair'
              : 'poor',
      sampleSize: operationTimes.length,
      meetsMinSample: operationTimes.length >= (qualityOptions.minSampleSize || 3),
    };
  }, [analysis, qualityOptions.minSampleSize]);

  return {
    analysis,
    isLoading,
    error,
    hasEnoughData: hasEnoughData && (qualityMetrics?.meetsMinSample ?? false),
    sampleSize,
    qualityMetrics,
    metadata,
  };
}

/**
 * Hook to get the raw censored analysis result when needed
 * This is useful for components that need access to censored data points
 */
export function useCensoredWeibullAnalysis(
  filters: FailureFilters = DEFAULT_FAILURE_FILTERS,
  confidenceLevel: number = 0.95
) {
  // Get complete failure dataset for analysis using real API
  const {
    data: analysisData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['failures-analysis', filters],
    queryFn: () => failuresApi.getFailuresForWeibullAnalysis(filters),
    staleTime: 60000, // 1 minute
    cacheTime: 600000, // 10 minutes
    refetchOnWindowFocus: false,
  });
  // Calculate censored Weibull analysis
  const censoredAnalysis = useMemo((): CensoredWeibullResult | null => {
    if (!analysisData?.failures || analysisData.failures.length < 3) {
      return null;
    }

    try {
      return calculateCensoredWeibullParameters(analysisData.failures, confidenceLevel);
    } catch (error) {
      console.error('Censored Weibull analysis failed:', error);
      return null;
    }
  }, [analysisData, confidenceLevel]);

  // Calculate analysis metadata
  const metadata = useMemo(() => {
    if (!analysisData?.failures) return null;

    const censoredCount = analysisData.failures.filter((f) => isCensoredObservation(f)).length;
    const failureCount = analysisData.failures.length - censoredCount;

    return {
      totalSamples: analysisData.failures.length,
      failureCount,
      censoredCount,
      censoringPercentage: (censoredCount / analysisData.failures.length) * 100,
      hasCensoredData: censoredCount > 0,
    };
  }, [analysisData]);

  // Calculate diversity metrics from the same dataset
  const diversityMetrics = useMemo(() => {
    if (!analysisData?.failures) {
      return {
        uniqueSites: 0,
        uniqueTurbines: 0,
        uniqueComponents: 0,
      };
    }

    return {
      uniqueSites: new Set(analysisData.failures.map((f) => f.site_name)).size,
      uniqueTurbines: new Set(analysisData.failures.map((f) => f.turbine_name)).size,
      uniqueComponents: new Set(analysisData.failures.map((f) => f.component_major)).size,
    };
  }, [analysisData]);

  return {
    analysis: censoredAnalysis,
    standardAnalysis: censoredAnalysis ? convertCensoredResult(censoredAnalysis) : null,
    isLoading,
    error,
    hasEnoughData: (analysisData?.failures?.length || 0) >= 3,
    sampleSize: analysisData?.failures?.length || 0,
    metadata,
    diversityMetrics,
  };
}
